import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import API_ENDPOINTS from '../config/api'
import {
  deriveTranslationPropagationPlan,
  summarizeTranslationFragmentDiagnostics,
  type TranslationCharAssignment,
} from '../lib/workbenchTranslationAlignment'

export interface WorkbenchPropagationDiagnostics {
  targetWitnessId: string
  modelId: string
  prompt: string
  systemInstruction: string
  generationConfig: Record<string, unknown>
  rawText: string
  finishReason?: string | null
  rawResponse?: Record<string, unknown> | null
  parseStatus: string
  parseError?: string | null
}

export interface WorkbenchPropagationFailure {
  witnessId: string
  error: string
  diagnostics?: WorkbenchPropagationDiagnostics
}

export interface WorkbenchPropagationPreviewPlanResponse {
  witnessId: string
  method: string
  strategy?: 'ledger-conditioned' | 'translation-aligned' | 'ledger-span-aligned'
  splitIndices: number[]
  exemplarWitnessIds: string[]
  rowAssignments: Array<{
    rowId: string
    segmentIndex: number | null
    startTokenIndex?: number
    endTokenIndex?: number
    translationStatus?: 'mapped' | 'gap' | 'uncertain' | 'transposed'
  }>
  confidence?: string
  notes?: string
  diagnostics?: WorkbenchPropagationDiagnostics
}

export interface WorkbenchPropagationPreviewResult {
  plans: WorkbenchPropagationPreviewPlanResponse[]
  failures: WorkbenchPropagationFailure[]
}

export interface WorkbenchTranslationBatchPreviewResult {
  witnessId: string
  method: string
  batchIndex: number
  totalBatches: number
  nextStartCharFloor: number
  nextBatchIndex: number
  nextRowOffset: number
  complete: boolean
  partial: boolean
  error?: string
  failedRowId?: string
  charAssignments: TranslationCharAssignment[]
  diagnostics?: WorkbenchPropagationDiagnostics
  repairCandidateRowIds?: string[]
}

export interface TranslationBatchRetryAttempt {
  batchIndex: number
  rowOffset: number
  error: string
  automatic: boolean
  createdAt: number
  failedRowId?: string
  diagnostics?: WorkbenchPropagationDiagnostics
}

export interface TranslationBatchResumeState {
  batchIndex: number
  rowOffset: number
  totalBatches: number
  nextStartCharFloor: number
  charAssignments: TranslationCharAssignment[]
  batchDiagnostics: WorkbenchPropagationDiagnostics[]
  failedRowId?: string
  retryHistory?: TranslationBatchRetryAttempt[]
}

export class WorkbenchPropagationPreviewError extends Error {
  failures: WorkbenchPropagationFailure[]

  constructor(message: string, failures: WorkbenchPropagationFailure[] = []) {
    super(message)
    this.name = 'WorkbenchPropagationPreviewError'
    this.failures = failures
  }
}

export class WorkbenchTranslationBatchPreviewError extends Error {
  witnessId: string
  diagnostics?: WorkbenchPropagationDiagnostics

  constructor(message: string, witnessId: string, diagnostics?: WorkbenchPropagationDiagnostics) {
    super(message)
    this.name = 'WorkbenchTranslationBatchPreviewError'
    this.witnessId = witnessId
    this.diagnostics = diagnostics
  }
}

export type WorkbenchInferenceRuntime = 'gemini' | 'pi'
export type WorkbenchThinkingLevel = 'off' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh'

export interface RequestWorkbenchPropagationPreviewInput {
  witnesses: Witness[]
  rows: AnchoredRow[]
  targetWitnessIds: string[]
  alignmentStrategy?: 'auto' | 'ledger-conditioned' | 'translation-aligned' | 'ledger-span-aligned'
  inferenceRuntime?: WorkbenchInferenceRuntime
  modelProvider?: string
  thinkingLevel?: WorkbenchThinkingLevel
  modelId?: string
}

export interface RequestWorkbenchTranslationBatchPreviewInput {
  witnesses: Witness[]
  rows: AnchoredRow[]
  targetWitnessId: string
  batchIndex: number
  startCharFloor: number
  rowOffset?: number
  batchSize?: number
  repairTargetRowIds?: string[]
  repairCandidateRowIds?: string[]
  repairTargetSpan?: {
    startChar: number
    endChar: number
    text: string
  }
  alignmentStrategy?: 'translation-aligned' | 'ledger-span-aligned'
  inferenceRuntime?: WorkbenchInferenceRuntime
  modelProvider?: string
  thinkingLevel?: WorkbenchThinkingLevel
  modelId?: string
}

const toRequestWitness = (witness: Witness) => ({
  id: witness.id,
  sigla: witness.sigla,
  language: witness.language || null,
  uploaded_file: {
    content: witness.uploaded_file?.content || '',
  },
})

const toRequestRow = (row: AnchoredRow) => ({
  id: row.id,
  unitMappings: row.unitMappings.map(mapping => ({
    witnessId: mapping.witnessId,
    textSnapshot: mapping.textSnapshot || '',
    excluded: Boolean(mapping.excluded),
  })),
})

const PREVIEW_TIMEOUT_MS = 930_000

const mapDiagnostics = (diagnostics: {
  target_witness_id: string
  model_id: string
  prompt: string
  system_instruction: string
  generation_config: Record<string, unknown>
  raw_text: string
  finish_reason?: string | null
  raw_response?: Record<string, unknown> | null
  parse_status: string
  parse_error?: string | null
}): WorkbenchPropagationDiagnostics => ({
  targetWitnessId: diagnostics.target_witness_id,
  modelId: diagnostics.model_id,
  prompt: diagnostics.prompt,
  systemInstruction: diagnostics.system_instruction,
  generationConfig: diagnostics.generation_config,
  rawText: diagnostics.raw_text,
  finishReason: diagnostics.finish_reason,
  rawResponse: diagnostics.raw_response,
  parseStatus: diagnostics.parse_status,
  parseError: diagnostics.parse_error,
})

const fetchWithTimeout = async (input: RequestInfo | URL, init: RequestInit, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Propagation preview timed out after ${Math.round(timeoutMs / 1000)}s`)
    }
    throw error
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export const requestWorkbenchPropagationPreview = async (
  input: RequestWorkbenchPropagationPreviewInput,
): Promise<WorkbenchPropagationPreviewResult> => {
  const response = await fetchWithTimeout(API_ENDPOINTS.workbenchPropagation.preview, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      witnesses: input.witnesses.map(toRequestWitness),
      rows: input.rows.map(toRequestRow),
      target_witness_ids: input.targetWitnessIds,
      alignment_strategy: input.alignmentStrategy || 'auto',
      ...(input.inferenceRuntime ? { inference_runtime: input.inferenceRuntime } : {}),
      ...(input.modelProvider?.trim() ? { model_provider: input.modelProvider.trim() } : {}),
      ...(input.thinkingLevel ? { thinking_level: input.thinkingLevel } : {}),
      ...(input.modelId?.trim() ? { model_id: input.modelId.trim() } : {}),
    }),
  }, PREVIEW_TIMEOUT_MS)

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    try {
      const payload = JSON.parse(detail) as {
        detail?: string
        failures?: Array<{
          witness_id: string
          error: string
          diagnostics?: {
            target_witness_id: string
            model_id: string
            prompt: string
            system_instruction: string
            generation_config: Record<string, unknown>
            raw_text: string
            finish_reason?: string | null
            raw_response?: Record<string, unknown> | null
            parse_status: string
            parse_error?: string | null
          }
        }>
      }
      if (Array.isArray(payload.failures) && payload.failures.length > 0) {
        throw new WorkbenchPropagationPreviewError(
          payload.detail || payload.failures[0]?.error || 'Failed to build workbench propagation preview',
          payload.failures.map(failure => ({
            witnessId: failure.witness_id,
            error: failure.error,
            ...(failure.diagnostics ? {
              diagnostics: {
                ...mapDiagnostics(failure.diagnostics),
              }
            } : {})
          })),
        )
      }
    } catch (error) {
      if (error instanceof WorkbenchPropagationPreviewError) {
        throw error
      }
    }
    throw new Error(detail || 'Failed to build workbench propagation preview')
  }

    const payload = await response.json() as {
      plans?: Array<{
        witness_id: string
        method: string
        strategy?: 'ledger-conditioned' | 'translation-aligned' | 'ledger-span-aligned'
        split_indices?: number[]
        exemplar_witness_ids?: string[]
        row_assignments?: Array<{
          row_id: string
          segment_index: number
          start_token_index: number
          end_token_index: number
          translation_status?: 'mapped' | 'gap' | 'uncertain' | 'transposed'
        }>
        char_assignments?: Array<{
          row_id: string
          status: 'mapped' | 'gap' | 'uncertain' | 'transposed'
          span_text?: string
          start_char?: number | null
          end_char?: number | null
          confidence?: string | null
          notes?: string | null
        }>
        diagnostics?: {
          target_witness_id: string
          model_id: string
          prompt: string
          system_instruction: string
          generation_config: Record<string, unknown>
          raw_text: string
          finish_reason?: string | null
          raw_response?: Record<string, unknown> | null
          parse_status: string
          parse_error?: string | null
        }
      }>
      failures?: Array<{
        witness_id: string
        error: string
        diagnostics?: {
          target_witness_id: string
          model_id: string
          prompt: string
          system_instruction: string
          generation_config: Record<string, unknown>
          raw_text: string
          finish_reason?: string | null
          raw_response?: Record<string, unknown> | null
          parse_status: string
          parse_error?: string | null
        }
      }>
    }

  const witnessById = new Map(input.witnesses.map(witness => [witness.id, witness]))

  const plans = (payload.plans || []).map(plan => {
    const strategy = plan.strategy
    const charAssignments: TranslationCharAssignment[] = Array.isArray(plan.char_assignments)
      ? plan.char_assignments.map(assignment => ({
          rowId: assignment.row_id,
          status: assignment.status,
          spanText: assignment.span_text || '',
          startChar: typeof assignment.start_char === 'number' ? assignment.start_char : null,
          endChar: typeof assignment.end_char === 'number' ? assignment.end_char : null,
          ...(assignment.confidence ? { confidence: assignment.confidence } : {}),
          ...(assignment.notes ? { notes: assignment.notes } : {}),
        }))
      : []
    const translationTargetWitness = witnessById.get(plan.witness_id)
    const derivedTranslationPlan = (strategy === 'translation-aligned' || strategy === 'ledger-span-aligned') && translationTargetWitness
      ? deriveTranslationPropagationPlan({
          text: translationTargetWitness.uploaded_file?.content || '',
          charAssignments,
        })
      : null
    const translationCoverageSummary = derivedTranslationPlan
      ? summarizeTranslationFragmentDiagnostics(derivedTranslationPlan.fragmentDiagnostics)
      : null

    return {
      witnessId: plan.witness_id,
      method: plan.method,
      ...(strategy ? { strategy } : {}),
      splitIndices: derivedTranslationPlan
        ? derivedTranslationPlan.splitIndices
        : (Array.isArray(plan.split_indices) ? plan.split_indices : []),
      exemplarWitnessIds: Array.isArray(plan.exemplar_witness_ids) ? plan.exemplar_witness_ids : [],
      rowAssignments: derivedTranslationPlan
        ? derivedTranslationPlan.rowAssignments
        : (
            Array.isArray(plan.row_assignments)
              ? plan.row_assignments.map(assignment => ({
                  rowId: assignment.row_id,
                  segmentIndex: assignment.segment_index,
                  startTokenIndex: assignment.start_token_index,
                  endTokenIndex: assignment.end_token_index,
                  translationStatus: assignment.translation_status,
                }))
              : []
          ),
      ...(charAssignments[0]?.confidence ? { confidence: charAssignments[0].confidence } : {}),
      ...((charAssignments.some(assignment => assignment.notes) || translationCoverageSummary)
        ? {
            notes: [
              ...charAssignments.map(assignment => assignment.notes).filter(Boolean),
              ...(translationCoverageSummary ? [translationCoverageSummary] : []),
            ].join(' · ')
          }
        : {}),
      ...(plan.diagnostics ? {
        diagnostics: mapDiagnostics(plan.diagnostics)
      } : {})
    }
  })

  return {
    plans,
    failures: Array.isArray(payload.failures)
      ? payload.failures.map(failure => ({
          witnessId: failure.witness_id,
          error: failure.error,
          ...(failure.diagnostics ? {
            diagnostics: mapDiagnostics(failure.diagnostics)
          } : {})
        }))
      : [],
  }
}

export const requestWorkbenchTranslationBatchPreview = async (
  input: RequestWorkbenchTranslationBatchPreviewInput,
): Promise<WorkbenchTranslationBatchPreviewResult> => {
  const response = await fetchWithTimeout(API_ENDPOINTS.workbenchPropagation.translationBatch, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      witnesses: input.witnesses.map(toRequestWitness),
      rows: input.rows.map(toRequestRow),
      target_witness_id: input.targetWitnessId,
      batch_index: input.batchIndex,
      start_char_floor: input.startCharFloor,
      row_offset: typeof input.rowOffset === 'number' ? input.rowOffset : 0,
      ...(typeof input.batchSize === 'number' ? { batch_size: input.batchSize } : {}),
      ...(input.repairTargetRowIds && input.repairTargetRowIds.length > 0 ? { repair_target_row_ids: input.repairTargetRowIds } : {}),
      ...(input.repairCandidateRowIds && input.repairCandidateRowIds.length > 0 ? { repair_candidate_row_ids: input.repairCandidateRowIds } : {}),
      ...(input.repairTargetSpan ? {
        repair_target_span: {
          start_char: input.repairTargetSpan.startChar,
          end_char: input.repairTargetSpan.endChar,
          text: input.repairTargetSpan.text,
        },
      } : {}),
      alignment_strategy: input.alignmentStrategy || 'translation-aligned',
      ...(input.inferenceRuntime ? { inference_runtime: input.inferenceRuntime } : {}),
      ...(input.modelProvider?.trim() ? { model_provider: input.modelProvider.trim() } : {}),
      ...(input.thinkingLevel ? { thinking_level: input.thinkingLevel } : {}),
      ...(input.modelId?.trim() ? { model_id: input.modelId.trim() } : {}),
    }),
  }, PREVIEW_TIMEOUT_MS)

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    try {
      const payload = JSON.parse(detail) as {
        detail?: string | {
          message?: string
          witness_id?: string
          diagnostics?: {
            target_witness_id: string
            model_id: string
            prompt: string
            system_instruction: string
            generation_config: Record<string, unknown>
            raw_text: string
            finish_reason?: string | null
            raw_response?: Record<string, unknown> | null
            parse_status: string
            parse_error?: string | null
          }
        }
      }
      const nestedDetail = payload.detail
      if (nestedDetail && typeof nestedDetail === 'object' && nestedDetail.message) {
        throw new WorkbenchTranslationBatchPreviewError(
          nestedDetail.message,
          nestedDetail.witness_id || input.targetWitnessId,
          nestedDetail.diagnostics ? mapDiagnostics(nestedDetail.diagnostics) : undefined,
        )
      }
    } catch (error) {
      if (error instanceof WorkbenchTranslationBatchPreviewError) {
        throw error
      }
    }
    throw new Error(detail || 'Failed to build translation alignment preview batch')
  }

  const payload = await response.json() as {
    witness_id: string
    method: string
    batch_index: number
    total_batches: number
    next_start_char_floor: number
    next_batch_index: number
    next_row_offset: number
    complete: boolean
    partial?: boolean
    error?: string | null
    failed_row_id?: string | null
    repair_candidate_row_ids?: string[]
    char_assignments?: Array<{
      row_id: string
      status: 'mapped' | 'gap' | 'uncertain' | 'transposed'
      span_text?: string
      start_char?: number | null
      end_char?: number | null
      confidence?: string | null
      notes?: string | null
    }>
    diagnostics?: {
      target_witness_id: string
      model_id: string
      prompt: string
      system_instruction: string
      generation_config: Record<string, unknown>
      raw_text: string
      finish_reason?: string | null
      raw_response?: Record<string, unknown> | null
      parse_status: string
      parse_error?: string | null
    }
  }

  return {
    witnessId: payload.witness_id,
    method: payload.method,
    batchIndex: payload.batch_index,
    totalBatches: payload.total_batches,
    nextStartCharFloor: payload.next_start_char_floor,
    nextBatchIndex: payload.next_batch_index,
    nextRowOffset: payload.next_row_offset,
    complete: Boolean(payload.complete),
    partial: Boolean(payload.partial),
    ...(payload.error ? { error: payload.error } : {}),
    ...(payload.failed_row_id ? { failedRowId: payload.failed_row_id } : {}),
    ...(Array.isArray(payload.repair_candidate_row_ids) ? {
      repairCandidateRowIds: payload.repair_candidate_row_ids.filter(
        (rowId): rowId is string => typeof rowId === 'string' && Boolean(rowId)
      ),
    } : {}),
    charAssignments: Array.isArray(payload.char_assignments)
      ? payload.char_assignments.map(assignment => ({
          rowId: assignment.row_id,
          status: assignment.status,
          spanText: assignment.span_text || '',
          startChar: typeof assignment.start_char === 'number' ? assignment.start_char : null,
          endChar: typeof assignment.end_char === 'number' ? assignment.end_char : null,
          ...(assignment.confidence ? { confidence: assignment.confidence } : {}),
          ...(assignment.notes ? { notes: assignment.notes } : {}),
        }))
      : [],
    ...(payload.diagnostics ? { diagnostics: mapDiagnostics(payload.diagnostics) } : {}),
  }
}
